import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../auth';
import { useFlexibleTranslation } from '../contexts/LanguageContext';

const stripePromise = loadStripe('pk_test_51RiybZQhk23F6covbw2TpoPUrM8EXfPZHNNBDNTFGGOWES0WTAO3FV55XkxR0hwCtd0DUIRDVqnaVbggApIPFYKF00kNLiNdJa');

const plans = {
  monthly: [
    { name: 'CODiii Solo', priceDisplay: 'CA$75', priceId: 'price_1RiyelQhk23F6cov7CCRK0ci' },
    { name: 'CODiii Studio', priceDisplay: 'CA$120', priceId: 'price_1RiygIQhk23F6covj0pkM4ig' },
  ],
  yearly: [
    { name: 'CODiii Solo', priceDisplay: 'CA$720', priceId: 'price_1RiyfTQhk23F6covy0TrpWhW' },
    { name: 'CODiii Studio', priceDisplay: 'CA$1152', priceId: 'price_1RiygXQhk23F6cov6reKwqsr' },
  ],
};

// Updated styles
const styles = {
  wrapper: {
    fontFamily: 'Inter, sans-serif',
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: '80px',
  },
  logo: {
    width: '200px',
    marginTop: '40px',
    marginBottom: '20px',
  },
  container: {
    maxWidth: '960px',
    width: '100%',
    padding: '0 24px',
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center', // ensures center alignment for toggle
  },
  title: {
    fontSize: '34px',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: '30px',
    color: '#1f2937',
  },
  toggleWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '40px',
    background: '#e5e7eb',
    borderRadius: '999px',
    padding: '4px',
  },
  toggleButton: (active) => ({
    padding: '10px 28px',
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '999px',
    backgroundColor: active ? '#ff7b00' : 'transparent',
    color: active ? '#fff' : '#374151',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
  }),
  cards: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '36px',
  },
  card: {
    backgroundColor: '#fff',
    padding: '36px 28px',
    borderRadius: '18px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
    width: '300px',
    textAlign: 'center',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
  },
  cardHover: {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
  },
  titleText: {
    fontSize: '22px',
    fontWeight: 700,
    marginBottom: '14px',
    color: '#111827',
    textShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
  },  
  price: {
    fontSize: '32px',
    fontWeight: 800,
    color: '#111827',
    marginBottom: '6px',
  },
  subtext: {
    fontSize: '15px',
    color: '#6b7280',
    marginBottom: '30px',
  },
  button: {
    backgroundColor: '#ff7b00',
    color: '#fff',
    padding: '12px 0',
    width: '100%',
    fontWeight: 600,
    borderRadius: '999px',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, background-color 0.2s ease',
  },
  buttonHover: {
    transform: 'scale(1.04)',
    backgroundColor: '#e66f00',
  },
  savingsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '16px',
  },
  
  savingsText: {
    fontSize: '14px',
    color: '#ff7b00', // CODiii accent color
    fontWeight: 500,
  },
  
  switchToggle: {
    width: '36px',
    height: '20px',
    backgroundColor: '#e5e7eb',
    borderRadius: '999px',
    position: 'relative',
    cursor: 'pointer',
    border: '1px solid #d1d5db',
    transition: 'background-color 0.2s ease',
  },
  
  switchThumb: {
    width: '16px',
    height: '16px',
    backgroundColor: '#fff',
    borderRadius: '999px',
    position: 'absolute',
    top: '1px',
    left: '1px',
    transition: 'transform 0.2s ease',
  },
  
  switchThumbActive: {
    transform: 'translateX(16px)',
  },  
  
};


const parseJwt = (token) => {
  try {
    const base64Payload = token.split('.')[1];
    const jsonPayload = atob(base64Payload);
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Invalid JWT:", e);
    return null;
  }
};

const Subscription = () => {
  const { t } = useFlexibleTranslation();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const { user } = useAuth();

  const handleSubscribe = async (priceId) => {
    try {
      const userInfo = parseJwt(user?.id_token);
      const sub = userInfo?.sub;
      if (!sub) throw new Error("Missing sub");

      const response = await fetch('https://fqv7jmmcz5.execute-api.ca-central-1.amazonaws.com/dev/createCheckoutSession', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sub, priceId })
      });

      const data = await response.json();
      if (!data.sessionId) throw new Error('No sessionId returned');

      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId: data.sessionId });

    } catch (err) {
      console.error('Subscription error:', err);
      alert(t('subscription.failedToStart', 'Failed to start subscription. Please try again.'));
    }
  };

  const getSavings = (planName) => {
    if (planName === 'CODiii Solo') return 'CA$180';
    if (planName === 'CODiii Studio') return 'CA$288';
    return '';
  };
  

  return (
    <div style={styles.wrapper}>
      <img src="/CODiii_PrimaryGradient1.png" alt={t('subscription.codiiiLogo', 'CODiii Logo')} style={styles.logo} />

      <div style={styles.container}>
        <div style={styles.title}>{t('subscription.subscriptionPlans', 'Subscription Plans')}</div>

        <div style={styles.toggleWrapper}>
          <button
            style={styles.toggleButton(billingCycle === 'monthly')}
            onClick={() => setBillingCycle('monthly')}
          >
            {t('subscription.monthly', 'Monthly')}
          </button>
          <button
            style={styles.toggleButton(billingCycle === 'yearly')}
            onClick={() => setBillingCycle('yearly')}
          >
            {t('subscription.yearly', 'Yearly')}
          </button>
        </div>

        <div style={styles.cards}>
          {plans[billingCycle].map((plan, index) => (
            <div
              key={plan.name}
              style={{
                ...styles.card,
                ...(hoveredCard === index ? styles.cardHover : {}),
              }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={styles.titleText}>{plan.name}</div>
              <div style={styles.price}>{plan.priceDisplay}</div>
              <div style={styles.subtext}>{t('subscription.perPeriod', 'per {{period}}', { period: billingCycle.slice(0, -2) })}</div>

            {/* Only show "save if you switch" message for monthly */}
            {billingCycle === 'monthly' && (
  <div style={styles.savingsRow}>
    <div style={styles.savingsText}>
      {t('subscription.saveBySwitching', 'Save {{savings}} by switching to annual', { savings: getSavings(plan.name) })}
    </div>
    <div
      style={styles.switchToggle}
      onClick={() => {
        setBillingCycle('yearly');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
    >
      <div
        style={{
          ...styles.switchThumb,
          ...(billingCycle === 'yearly' ? styles.switchThumbActive : {}),
        }}
      />
    </div>
  </div>
)}


            <button
              style={{
                ...styles.button,
                ...(hoveredBtn === index ? styles.buttonHover : {}),
              }}
              onMouseEnter={() => setHoveredBtn(index)}
              onMouseLeave={() => setHoveredBtn(null)}
              onClick={() => handleSubscribe(plan.priceId)}
            >
              {t('subscription.subscribe', 'Subscribe')}
            </button>

            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Subscription;


//TODO: make sure the remaining attributes are also updated in cognito when switching to payments